import React, { useState, useEffect } from 'react';
import {
  Menu, X, MessageCircle, MessageSquare, Bot, Users, Zap, FileText,
  CheckCircle, ChevronLeft, ChevronRight, TrendingUp, Clock, Calendar, Briefcase, Scale, LayoutDashboard, Plus, Bell,
  Gavel, ClipboardList, Landmark, PenLine, Trash2, Phone, Mail, User as UserIcon, Maximize2, LogOut, Edit3
} from 'lucide-react';
import './Dashboard.css';
import { supabase } from '../supabaseClient';
import AddEventModal from './AddEventModal';
import logo from '../assets/logo.svg';

// Robust avatar component with fallback to initials on image error
function ClientAvatar({ name = '', photoUrl = '', size = 36, radius = '50%', fontSize = 13 }) {
  const [imgErr, setImgErr] = React.useState(false);
  React.useEffect(() => { setImgErr(false); }, [photoUrl]);
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  const style = {
    width: size, height: size, borderRadius: radius,
    background: 'linear-gradient(135deg,#3D6AFF,#7B5EA7)',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize, flexShrink: 0, overflow: 'hidden',
  };
  return (
    <div style={style}>
      {photoUrl && !imgErr
        ? <img src={photoUrl} alt={name} onError={() => setImgErr(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: radius }} />
        : initial}
    </div>
  );
}

export default function Dashboard({ onNavigate, userRole = 'client', user, onOpenWorkspace, onLogout, hideSidebar = false }) {
  const NOTIFICATION_SEEN_TTL_MS = 24 * 60 * 60 * 1000;
  const NOTIFICATION_LIST_LIMIT = 20;
  const CLIENT_REMINDER_MAX_CHARS = 420;

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [events, setEvents] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCalendarExpanded, setIsCalendarExpanded] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [clients, setClients] = useState([]);
  const [isClientsExpanded, setIsClientsExpanded] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showAddClientForm, setShowAddClientForm] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', email: '', phone: '', caseType: '', caseDetails: '', specialization: '', barCouncilId: '', experience: '' });
  const [notifications, setNotifications] = useState([]);
  const [dismissedNotificationIds, setDismissedNotificationIds] = useState({});
  const [seenNotifications, setSeenNotifications] = useState({}); // { [notificationId]: ISOString }
  const [expandedNotificationMessages, setExpandedNotificationMessages] = useState({});
  const [workspaceUnreadByRelationship, setWorkspaceUnreadByRelationship] = useState({});
  const [workspaceLastSeenByRelationship, setWorkspaceLastSeenByRelationship] = useState({});
  const [nowTick, setNowTick] = useState(Date.now());
  const [localCompletedEvents, setLocalCompletedEvents] = useState({});
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [isTasksExpanded, setIsTasksExpanded] = useState(false);
  const [rescheduleTargetEvent, setRescheduleTargetEvent] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({ start_date: '', start_time: '09:00', end_time: '' });
  const [rescheduleError, setRescheduleError] = useState('');
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [isTopNotificationsOpen, setIsTopNotificationsOpen] = useState(false);
  const [isTopProfileMenuOpen, setIsTopProfileMenuOpen] = useState(false);
  const [clientReminderNote, setClientReminderNote] = useState('');
  const [isReminderHydrated, setIsReminderHydrated] = useState(false);

  const topNotificationsRef = React.useRef(null);
  const topProfileRef = React.useRef(null);

  const upsertNotificationState = React.useCallback(async (notificationId, payload = {}) => {
    if (!user?.id || !notificationId) return;
    const { error } = await supabase
      .from('user_notification_states')
      .upsert(
        {
          user_id: user.id,
          notification_id: notificationId,
          updated_at: new Date().toISOString(),
          ...payload,
        },
        { onConflict: 'user_id,notification_id' }
      );

    if (error) {
      console.error('Failed to persist notification state:', error);
    }
  }, [user?.id]);

  const upsertWorkspaceSeenState = React.useCallback(async (relationshipId, seenAt) => {
    if (!user?.id || !relationshipId || !seenAt) return;
    const { error } = await supabase
      .from('workspace_notification_states')
      .upsert(
        {
          user_id: user.id,
          relationship_id: relationshipId,
          last_seen_at: seenAt,
          updated_at: seenAt,
        },
        { onConflict: 'user_id,relationship_id' }
      );

    if (error) {
      console.error('Failed to persist workspace notification state:', error);
    }
  }, [user?.id]);

  // Role-based labels
  const peopleLabel = userRole === 'lawyer' ? 'Clients' : 'My Lawyers';
  const personLabel = userRole === 'lawyer' ? 'Client' : 'Lawyer';

  // Get user's first name or full name
  const userName = user?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'User';

  // Parse 'YYYY-MM-DD' as local date (not UTC)
  const parseLocalDate = (dateStr) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  const parseLocalDateTime = (dateStr, timeStr = '00:00') => {
    if (!dateStr) return null;
    const [y, m, d] = String(dateStr).split('-').map(Number);
    const [h, min] = String(timeStr || '00:00').split(':').map(Number);
    return new Date(y, (m || 1) - 1, d || 1, h || 0, min || 0, 0, 0);
  };

  const getDateTimeRange = (dateStr, startTime, endTime) => {
    const start = parseLocalDateTime(dateStr, startTime || '00:00');
    if (!start) return null;

    let end = endTime
      ? parseLocalDateTime(dateStr, endTime)
      : new Date(start.getTime() + 60 * 1000);

    if (end <= start) {
      end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
    }

    return { start, end };
  };

  const hasTimeOverlap = (a, b) => a.start < b.end && a.end > b.start;

  const formatLocalDateKey = (dateObj) => {
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const d = String(dateObj.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getEventTimeRange = (event) => {
    const start = new Date(`${event.start_date}T${event.start_time || '00:00'}`);
    let end = event.end_time
      ? new Date(`${event.start_date}T${event.end_time}`)
      : start;

    // If end is earlier than start, treat as overnight event.
    if (end < start) {
      end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
    }

    return { start, end };
  };

  const isEventCompleted = (event) => {
    if (localCompletedEvents[event.id]) return true;
    if (!event.status) return false;
    return ['completed', 'cancelled', 'deleted'].includes(String(event.status).toLowerCase());
  };

  const isEventOngoing = (event) => {
    if (isEventCompleted(event)) return false;
    const now = new Date(nowTick);
    const { start, end } = getEventTimeRange(event);
    return now >= start && now <= end;
  };

  const isEventUpcomingOrOngoing = (event) => {
    if (isEventCompleted(event)) return false;
    const now = new Date(nowTick);
    const { end } = getEventTimeRange(event);
    return end >= now;
  };

  const getNotificationTypeFromEvent = (eventType) => {
    const map = {
      court_hearing: 'court_hearing',
      client_meeting: 'client_meeting',
      filing_deadline: 'filing_deadline',
      trial_date: 'trial_date',
      contract_review: 'contract_review',
      other: 'contract_review',
    };
    return map[eventType] || 'reminder';
  };

  const formatNotificationTime = (ts) => {
    if (!ts) return 'Just now';
    const diffMs = Date.now() - new Date(ts).getTime();
    const mins = Math.max(0, Math.floor(diffMs / 60000));
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const renderNotificationIcon = (type) => {
    // Heroicons-style static SVGs per notification type
    const iconMap = {
      court_hearing: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M4.5 6h15M7.5 6v5.25c0 1.8-1.2 3.375-2.9 3.85l-.85.24M16.5 6v5.25c0 1.8 1.2 3.375 2.9 3.85l.85.24M9 6h6" />
        </svg>
      ),
      client_meeting: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M10 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" transform="translate(2 0)" />
        </svg>
      ),
      filing_deadline: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 3v1.5M15.75 3v1.5M3.75 8.25h16.5M4.5 5.25h15a.75.75 0 0 1 .75.75v12.75a.75.75 0 0 1-.75.75h-15a.75.75 0 0 1-.75-.75V6a.75.75 0 0 1 .75-.75ZM12 11.25v4.5M12 15.75h.008v.008H12v-.008Z" />
        </svg>
      ),
      trial_date: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
      ),
      contract_review: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-8.25a.75.75 0 0 0-.75-.75H9.75L4.5 10.5v8.25c0 .414.336.75.75.75h13.5a.75.75 0 0 0 .75-.75v-1.5M9 5.25V10.5h-4.5M15.75 15.75 14.25 20.25h-1.5l-1.5-4.5M17.25 20.25h-6" />
        </svg>
      ),
      message: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3h6m-8.25 8.25H18a2.25 2.25 0 0 0 2.25-2.25V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v11.25A2.25 2.25 0 0 0 6 19.5Z" />
        </svg>
      ),
      case_message: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3h6m-8.25 8.25H18a2.25 2.25 0 0 0 2.25-2.25V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v11.25A2.25 2.25 0 0 0 6 19.5Z" />
        </svg>
      ),
      reminder: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.081 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
        </svg>
      ),
    };

    return iconMap[type] || iconMap.reminder;
  };

  const renderNotificationAvatar = (notif) => {
    if (notif?.photo_url) {
      return (
        <img
          src={notif.photo_url}
          alt={notif.sender_name || 'Sender'}
          style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
        />
      );
    }

    if (notif?.sender_name) {
      return (notif.sender_name.trim().charAt(0) || '?').toUpperCase();
    }

    return renderNotificationIcon(notif?.type);
  };

  const toggleNotificationMessage = (notifId) => {
    setExpandedNotificationMessages((prev) => ({ ...prev, [notifId]: !prev[notifId] }));
  };

  const pruneSeenNotifications = React.useCallback((seenMap, nowMs = Date.now()) => {
    const next = {};
    Object.entries(seenMap || {}).forEach(([id, seenAt]) => {
      const seenTs = new Date(seenAt).getTime();
      if (!Number.isNaN(seenTs) && (nowMs - seenTs) < NOTIFICATION_SEEN_TTL_MS) {
        next[id] = seenAt;
      }
    });
    return next;
  }, [NOTIFICATION_SEEN_TTL_MS]);

  const mergeNotificationsWithSeenRetention = React.useCallback((incoming, previous) => {
    const incomingList = incoming || [];
    const previousList = previous || [];
    const incomingIds = new Set(incomingList.map((n) => n.id));
    const nowMs = Date.now();

    const retainedSeen = previousList.filter((notif) => {
      if (!notif?.id || incomingIds.has(notif.id)) return false;
      if (dismissedNotificationIds[notif.id]) return false;

      const seenAt = seenNotifications[notif.id];
      if (!seenAt) return false;

      const seenTs = new Date(seenAt).getTime();
      return !Number.isNaN(seenTs) && (nowMs - seenTs) < NOTIFICATION_SEEN_TTL_MS;
    });

    return [...incomingList, ...retainedSeen].slice(0, NOTIFICATION_LIST_LIMIT);
  }, [dismissedNotificationIds, seenNotifications, NOTIFICATION_SEEN_TTL_MS, NOTIFICATION_LIST_LIMIT]);

  const markNotificationSeen = React.useCallback((notificationId) => {
    if (!notificationId) return;
    const seenAt = new Date().toISOString();

    setSeenNotifications((prev) => {
      const pruned = pruneSeenNotifications(prev);
      return { ...pruned, [notificationId]: seenAt };
    });

    setDismissedNotificationIds((prev) => {
      if (!prev[notificationId]) return prev;
      const next = { ...prev };
      delete next[notificationId];
      return next;
    });

    void upsertNotificationState(notificationId, {
      seen_at: seenAt,
      dismissed_at: null,
    });
  }, [pruneSeenNotifications, upsertNotificationState]);

  const markWorkspaceSeen = (relationshipId) => {
    if (!relationshipId) return;
    const seenAt = new Date().toISOString();

    setWorkspaceLastSeenByRelationship((prev) => {
      return { ...prev, [relationshipId]: seenAt };
    });
    setWorkspaceUnreadByRelationship((prev) => ({ ...prev, [relationshipId]: 0 }));

    void upsertWorkspaceSeenState(relationshipId, seenAt);
  };

  const openWorkspaceForRelationship = (client) => {
    if (!client) return;
    setSelectedClient(client);
    markWorkspaceSeen(client.id);
    if (onOpenWorkspace) onOpenWorkspace(client);
  };

  const handleNotificationClick = async (notif) => {
    if (!notif) return;

    markNotificationSeen(notif.id);

    if (notif.type === 'message') {
      const messageId = String(notif.id || '').startsWith('msg-') ? String(notif.id).slice(4) : null;
      if (messageId) {
        await supabase
          .from('messages')
          .update({ is_read: true })
          .eq('id', messageId);
      }
      onNavigate('messages', notif.conversation_id || null);
      return;
    }

    if (notif.type === 'case_message') {
      const relationship = clients.find((c) => c.id === notif.relationship_id);
      if (relationship) {
        openWorkspaceForRelationship(relationship);
      }
      return;
    }

    if (notif.event_id) {
      const matched = events.find((e) => e.id === notif.event_id);
      if (matched) {
        setSelectedEvent(matched);
      }
    }
  };

  const handleClearNotification = (notificationId, e) => {
    e.stopPropagation();
    setDismissedNotificationIds((prev) => ({ ...prev, [notificationId]: true }));
    const dismissedAt = new Date().toISOString();

    setSeenNotifications((prev) => {
      const next = { ...prev };
      delete next[notificationId];
      return next;
    });
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));

    void upsertNotificationState(notificationId, {
      dismissed_at: dismissedAt,
      seen_at: null,
    });
  };

  // Helper function to get events for a specific date
  const getEventsForDate = (date) => {
    const dateStr = formatLocalDateKey(date);
    return events.filter(event => event.start_date === dateStr && isEventUpcomingOrOngoing(event));
  };

  // Helper function to get upcoming events (from current month onwards, sorted)
  const getUpcomingEvents = () => {
    return events
      .filter((event) => isEventUpcomingOrOngoing(event))
      .sort((a, b) => {
        const aOngoing = isEventOngoing(a);
        const bOngoing = isEventOngoing(b);
        if (aOngoing !== bOngoing) return aOngoing ? -1 : 1;

        const aStart = getEventTimeRange(a).start;
        const bStart = getEventTimeRange(b).start;
        return aStart - bStart;
      });
  };

  // Get number of days in month
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  // Get first day of month
  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const [relatedProfiles, setRelatedProfiles] = useState({}); // { [uuid]: { full_name, photo_url } }

  // Load notification state from Supabase.
  useEffect(() => {
    if (!user?.id) {
      setSeenNotifications({});
      setDismissedNotificationIds({});
      setWorkspaceLastSeenByRelationship({});
      return;
    }

    const loadNotificationState = async () => {
      const [notifStateRes, workspaceStateRes] = await Promise.all([
        supabase
          .from('user_notification_states')
          .select('notification_id, seen_at, dismissed_at')
          .eq('user_id', user.id),
        supabase
          .from('workspace_notification_states')
          .select('relationship_id, last_seen_at')
          .eq('user_id', user.id),
      ]);

      if (notifStateRes.error || workspaceStateRes.error) {
        console.error('Failed to load notification state from Supabase:', notifStateRes.error || workspaceStateRes.error);
        setSeenNotifications({});
        setWorkspaceLastSeenByRelationship({});
        setDismissedNotificationIds({});
        return;
      }

      const nextSeen = {};
      const nextDismissed = {};
      (notifStateRes.data || []).forEach((row) => {
        if (!row?.notification_id) return;
        if (row.dismissed_at) {
          nextDismissed[row.notification_id] = true;
        }
        if (row.seen_at) {
          nextSeen[row.notification_id] = row.seen_at;
        }
      });

      const nextWorkspaceSeen = {};
      (workspaceStateRes.data || []).forEach((row) => {
        if (!row?.relationship_id || !row?.last_seen_at) return;
        nextWorkspaceSeen[row.relationship_id] = row.last_seen_at;
      });

      setSeenNotifications(pruneSeenNotifications(nextSeen, Date.now()));
      setDismissedNotificationIds(nextDismissed);
      setWorkspaceLastSeenByRelationship(nextWorkspaceSeen);
    };

    loadNotificationState();
  }, [user?.id, pruneSeenNotifications]);

  useEffect(() => {
    setNotifications((prev) => {
      const filtered = prev.filter((notif) => {
        const seenAt = seenNotifications[notif.id];
        if (!seenAt) return true;
        const seenTs = new Date(seenAt).getTime();
        return !Number.isNaN(seenTs) && (nowTick - seenTs) < NOTIFICATION_SEEN_TTL_MS;
      });
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [seenNotifications, nowTick, NOTIFICATION_SEEN_TTL_MS]);

  // Load events from Supabase for this user, then fetch related user profiles
  useEffect(() => {
    if (!user?.id) return;
    const loadEvents = async () => {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('user_id', user.id)
        .order('start_date', { ascending: true });
      if (!error && data) {
        setEvents(data);
        // Fetch profiles for all related_user_id values (client/lawyer in meetings)
        const relIds = [...new Set(data.map(e => e.related_user_id).filter(Boolean))];
        if (relIds.length > 0) {
          const { data: profiles } = await supabase
            .from('user_profiles')
            .select('id, full_name, photo_url, email')
            .in('id', relIds);
          const map = {};
          (profiles || []).forEach(p => {
            map[p.id] = {
              full_name: p.full_name || p.email?.split('@')[0] || 'Unknown',
              photo_url: p.photo_url || null,
              email: p.email || '',
            };
          });
          setRelatedProfiles(map);
        }
      }
    };
    loadEvents();
  }, [user?.id]);

  // Persist small per-user reminder notes to Supabase.
  useEffect(() => {
    if (!user?.id) {
      setClientReminderNote('');
      setIsReminderHydrated(false);
      return;
    }

    let isCancelled = false;

    const loadReminderNote = async () => {
      const { data, error } = await supabase
        .from('user_sticky_notes')
        .select('note_text')
        .eq('user_id', user.id)
        .maybeSingle();

      if (isCancelled) return;

      if (error) {
        console.error('Error loading reminder note from Supabase:', error);
        setClientReminderNote('');
        setIsReminderHydrated(true);
        return;
      }

      setClientReminderNote(data?.note_text || '');
      setIsReminderHydrated(true);
    };

    loadReminderNote();

    return () => {
      isCancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !isReminderHydrated) return;

    const saveTimer = setTimeout(async () => {
      const note = clientReminderNote || '';

      if (!note.trim()) {
        const { error } = await supabase
          .from('user_sticky_notes')
          .delete()
          .eq('user_id', user.id);
        if (error) {
          console.error('Error clearing reminder note in Supabase:', error);
        }
        return;
      }

      const { error } = await supabase
        .from('user_sticky_notes')
        .upsert(
          {
            user_id: user.id,
            note_text: note,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' }
        );

      if (error) {
        console.error('Error saving reminder note to Supabase:', error);
      }
    }, 500);

    return () => clearTimeout(saveTimer);
  }, [clientReminderNote, isReminderHydrated, user?.id]);

  // Keep clock-based widgets (upcoming expiry/reminders) fresh.
  useEffect(() => {
    const timer = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (topNotificationsRef.current && !topNotificationsRef.current.contains(event.target)) {
        setIsTopNotificationsOpen(false);
      }
      if (topProfileRef.current && !topProfileRef.current.contains(event.target)) {
        setIsTopProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // Load clients/lawyers relationships from Supabase
  useEffect(() => {
    if (!user?.id) return;
    const loadRelationships = async () => {
      const col = userRole === 'lawyer' ? 'lawyer_id' : 'client_id';
      const otherCol = userRole === 'lawyer' ? 'client_id' : 'lawyer_id';

      // Step 1: get relationship rows
      const { data: rels, error } = await supabase
        .from('user_relationships')
        .select('id, lawyer_id, client_id, case_type, case_details, specialization, bar_council_id, experience, status')
        .eq(col, user.id);

      if (error || !rels?.length) { setClients([]); return; }

      // Step 2: collect the other party's UUIDs
      const otherIds = [...new Set(rels.map(r => r[otherCol]).filter(Boolean))];

      // Step 3: fetch user_profiles for all those UUIDs
      let profiles = [];
      if (otherIds.length > 0) {
        const { data } = await supabase
          .from('user_profiles')
          .select('id, email, full_name, photo_url, phone, specialization, experience, bar_number')
          .in('id', otherIds);
        profiles = data || [];
      }

      // Step 4: fetch from advocates table (for lawyers — has photo_path)
      let advProfiles = [];
      if (otherIds.length > 0) {
        const { data } = await supabase
          .from('advocates')
          .select('user_id, name, photo_path')
          .in('user_id', otherIds);
        advProfiles = data || [];
      }

      // Build lookup maps
      const profileMap = {};
      (profiles || []).forEach(p => { profileMap[p.id] = p; });

      const advMap = {};
      (advProfiles || []).forEach(a => { advMap[a.user_id] = a; });

      // Step 5: shape the data
      const shaped = rels.map(rel => {
        const otherId = rel[otherCol];
        const profile = profileMap[otherId] || {};
        const adv = advMap[otherId] || {};

        const displayName = profile.full_name
          || adv.name
          || (profile.email ? profile.email.split('@')[0] : null)
          || 'Unknown';

        const displayPhoto = profile.photo_url || adv.photo_path || '';

        return {
          id: rel.id,
          lawyer_id: rel.lawyer_id,
          client_id: rel.client_id,
          name: displayName,
          email: profile.email || '',
          photo_url: displayPhoto,
          phone: profile.phone || '',
          caseType: rel.case_type || '',
          caseDetails: rel.case_details || '',
          specialization: profile.specialization || rel.specialization || '',
          barCouncilId: profile.bar_number || rel.bar_council_id || '',
          experience: profile.experience || rel.experience || '',
          status: rel.status || 'active',
        };
      });

      setClients(shaped);
    };
    loadRelationships();
  }, [user?.id, userRole]);

  // Handle adding new event — saved to Supabase
  // If it's a client_meeting with a selected client, also insert the event for the client
  const handleAddEvent = async (formData) => {
    if (!user?.id) return;
    const { selected_client_id, __confirmedConflict, ...eventData } = formData;

    if (!eventData.start_date || !eventData.start_time) {
      throw new Error('Date and start time are required');
    }

    const startAt = parseLocalDateTime(eventData.start_date, eventData.start_time);
    if (startAt && startAt < new Date()) {
      throw new Error('Cannot add a task in the past. Please choose a future date/time.');
    }

    const incomingRange = getDateTimeRange(eventData.start_date, eventData.start_time, eventData.end_time);
    if (incomingRange) {
      const conflictCount = events.filter((ev) => {
        if (!ev?.start_date) return false;
        const status = String(ev.status || '').toLowerCase();
        if (['completed', 'cancelled', 'deleted'].includes(status)) return false;

        const evRange = getDateTimeRange(ev.start_date, ev.start_time || '00:00', ev.end_time);
        if (!evRange) return false;
        return hasTimeOverlap(incomingRange, evRange);
      }).length;

      if (conflictCount > 0 && !__confirmedConflict) {
        throw new Error('This time conflicts with an existing task. Please confirm to proceed.');
      }
    }

    // For client_meeting, accept either relationship id or direct client user id.
    let clientUUID = null;
    if (eventData.event_type === 'client_meeting' && selected_client_id) {
      const rel = clients.find((c) => c.id === selected_client_id || c.client_id === selected_client_id);
      clientUUID = rel?.client_id || selected_client_id;
    }

    // Insert event for current user, storing related_user_id and reminder_minutes
    const { data, error } = await supabase
      .from('events')
      .insert([{
        ...eventData,
        user_id: user.id,
        related_user_id: clientUUID || null,
        reminder_minutes: parseInt(formData.reminder_minutes) || 0,
      }])
      .select()
      .single();
    if (!error && data) setEvents(prev => [...prev, data]);

    // Mirror event to client's calendar too
    if (!error && eventData.event_type === 'client_meeting' && clientUUID) {
      const { error: mirrorError } = await supabase.from('events').insert([{
        ...eventData,
        user_id: clientUUID,
        related_user_id: user.id,  // client sees the lawyer as related user
        title: eventData.title || 'Meeting with Lawyer',
        description: eventData.description || '',
        reminder_minutes: parseInt(formData.reminder_minutes) || 0,
      }]);

      if (mirrorError) {
        console.error('Failed to mirror client meeting to client calendar:', mirrorError);
      }
    }
  };

  // Handle deleting event — removed from Supabase
  const handleDeleteEvent = async (eventId) => {
    const { error } = await supabase
      .from('events')
      .delete()
      .eq('id', eventId);
    if (!error) {
      setEvents(prev => prev.filter(e => e.id !== eventId));
      setLocalCompletedEvents(prev => {
        const next = { ...prev };
        delete next[eventId];
        return next;
      });
    }
  };

  const handleMarkEventCompleted = async (event) => {
    if (!event?.id) return;

    // The current Supabase schema has no events.status column.
    // Persist completion by removing the event row from Supabase.
    setLocalCompletedEvents(prev => ({ ...prev, [event.id]: true }));

    const { error } = await supabase
      .from('events')
      .delete()
      .eq('id', event.id);

    if (error) {
      console.error('Failed to mark task as completed:', error);
      setLocalCompletedEvents(prev => {
        const next = { ...prev };
        delete next[event.id];
        return next;
      });
      return;
    }

    setEvents(prev => prev.filter(e => e.id !== event.id));
    setLocalCompletedEvents(prev => {
      const next = { ...prev };
      delete next[event.id];
      return next;
    });
  };

  const openRescheduleModal = (event) => {
    if (!event?.id) return;
    setRescheduleTargetEvent(event);
    setRescheduleForm({
      start_date: event.start_date || '',
      start_time: event.start_time || '09:00',
      end_time: event.end_time || '',
    });
    setRescheduleError('');
    setIsRescheduleModalOpen(true);
  };

  const closeRescheduleModal = () => {
    setIsRescheduleModalOpen(false);
    setRescheduleTargetEvent(null);
    setRescheduleError('');
    setIsRescheduling(false);
  };

  const handleRescheduleEvent = (event) => {
    openRescheduleModal(event);
  };

  const handleRescheduleSubmit = async (e) => {
    e.preventDefault();
    if (!rescheduleTargetEvent?.id) return;

    if (!rescheduleForm.start_date) {
      setRescheduleError('Date is required');
      return;
    }
    if (!rescheduleForm.start_time) {
      setRescheduleError('Start time is required');
      return;
    }

    const nextStart = parseLocalDateTime(rescheduleForm.start_date, rescheduleForm.start_time);
    if (nextStart && nextStart < new Date()) {
      setRescheduleError('Cannot reschedule a task to a past date/time');
      return;
    }

    let shouldForceConflict = false;
    const currentRange = getDateTimeRange(rescheduleForm.start_date, rescheduleForm.start_time, rescheduleForm.end_time);
    if (currentRange) {
      const hasConflict = events.some((ev) => {
        if (!ev?.id || ev.id === rescheduleTargetEvent.id || !ev.start_date) return false;
        const status = String(ev.status || '').toLowerCase();
        if (['completed', 'cancelled', 'deleted'].includes(status)) return false;

        const evRange = getDateTimeRange(ev.start_date, ev.start_time || '00:00', ev.end_time);
        if (!evRange) return false;
        return hasTimeOverlap(currentRange, evRange);
      });

      if (hasConflict) {
        const shouldProceed = window.confirm('This reschedule conflicts with another task. Do you want to proceed anyway?');
        if (!shouldProceed) return;
        shouldForceConflict = true;
      }
    }

    setIsRescheduling(true);
    setRescheduleError('');

    const { data, error } = await supabase
      .from('events')
      .update({
        start_date: rescheduleForm.start_date,
        start_time: rescheduleForm.start_time,
        end_time: rescheduleForm.end_time || null,
      })
      .eq('id', rescheduleTargetEvent.id)
      .select()
      .single();

    if (error || !data) {
      setRescheduleError(error?.message || 'Failed to reschedule task');
      setIsRescheduling(false);
      return;
    }

    setEvents(prev => prev.map(ev => (ev.id === rescheduleTargetEvent.id ? data : ev)));
    setLocalCompletedEvents(prev => {
      const next = { ...prev };
      delete next[rescheduleTargetEvent.id];
      return next;
    });

    if (selectedEvent?.id === rescheduleTargetEvent.id) {
      setSelectedEvent(data);
    }

    closeRescheduleModal();
  };

  // Open modal for date
  const openEventModal = (date) => {
    setSelectedDate(date);
    setIsModalOpen(true);
  };

  // Navigate months
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth()));
  };

  // Helper: get first day of month (Monday = 0)
  const getFirstDayMon = (date) => {
    const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return day === 0 ? 6 : day - 1; // Convert Sun=0 to Mon-start
  };

  // Helper: get previous month's days count
  const getPrevMonthDays = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 0).getDate();
  };

  // Client helpers
  const getClientMeetings = (clientName) => {
    return events.filter(event =>
      event.title?.toLowerCase().includes(clientName?.toLowerCase()) ||
      event.description?.toLowerCase().includes(clientName?.toLowerCase())
    );
  };

  const handleAddClient = async () => {
    if (!newClient.email.trim() || !user?.id) return;

    // Resolve other party by email so relationship stores BOTH id and email
    let resolvedOtherId = null;
    const targetEmail = newClient.email.trim();

    const { data: profileByEmail } = await supabase
      .from('user_profiles')
      .select('id, email, full_name, photo_url')
      .eq('email', targetEmail)
      .maybeSingle();

    if (profileByEmail?.id) {
      resolvedOtherId = profileByEmail.id;
    }
    // Note: no fallback to a 'users' table — that public table doesn't exist in this project.

    const row = userRole === 'lawyer'
      ? {
        lawyer_id: user.id,
        client_id: resolvedOtherId,
        case_type: newClient.caseType,
        case_details: newClient.caseDetails,
        status: 'active'
      }
      : {
        client_id: user.id,
        lawyer_id: resolvedOtherId,
        specialization: newClient.specialization,
        bar_council_id: newClient.barCouncilId,
        experience: newClient.experience,
        status: 'active'
      };

    const { data, error } = await supabase
      .from('user_relationships')
      .insert([row])
      .select()
      .single();
    if (!error && data) {
      // Try to resolve photo + display name for the newly added person
      let resolvedName = newClient.name || newClient.email;
      let resolvedPhoto = '';
      if (resolvedOtherId) {
        const { data: prof } = await supabase
          .from('user_profiles')
          .select('full_name, name, photo_url, photo_path')
          .eq('id', resolvedOtherId)
          .maybeSingle();
        if (prof) {
          resolvedName = prof.full_name || prof.name || resolvedName;
          resolvedPhoto = prof.photo_url || prof.photo_path || '';
        }
        if (!resolvedPhoto) {
          const { data: adv } = await supabase
            .from('advocates')
            .select('name, photo_path')
            .eq('user_id', resolvedOtherId)
            .maybeSingle();
          if (adv) {
            resolvedName = resolvedName || adv.name;
            resolvedPhoto = adv.photo_path || '';
          }
        }
      }
      setClients(prev => [...prev, {
        id: data.id,
        client_id: resolvedOtherId,
        name: resolvedName,
        email: newClient.email,
        photo_url: resolvedPhoto,
        caseType: newClient.caseType,
        caseDetails: newClient.caseDetails,
        specialization: newClient.specialization,
        barCouncilId: newClient.barCouncilId,
        experience: newClient.experience,
        status: 'active',
      }]);
    }
    setNewClient({ name: '', email: '', phone: '', caseType: '', caseDetails: '', specialization: '', barCouncilId: '', experience: '' });
    setShowAddClientForm(false);
  };

  const handleDeleteClient = async (clientId) => {
    const { error } = await supabase
      .from('user_relationships')
      .delete()
      .eq('id', clientId);
    if (!error) {
      setClients(prev => prev.filter(c => c.id !== clientId));
      if (selectedClient?.id === clientId) setSelectedClient(null);
    }
  };

  // Load user's profile photo by UUID
  React.useEffect(() => {
    if (!user?.id) return;
    const loadProfilePhoto = async () => {
      try {
        const { data } = await supabase
          .from('user_profiles')
          .select('photo_url')
          .eq('id', user.id)
          .single();
        if (data?.photo_url) setProfilePhoto(data.photo_url);
      } catch (error) {
        console.error('Error loading profile photo:', error);
      }
    };
    loadProfilePhoto();
  }, [user?.id]);

  // Navigation items - role-based filtering
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={24} />, active: true },
    { id: 'landing', label: 'Chatbot', icon: <Bot size={24} />, active: false },
    { id: 'messages', label: 'Messages', icon: <MessageSquare size={24} />, active: false },
    // Client-only: Advocate Profiles
    ...(userRole === 'client' ? [{ id: 'profiles', label: 'Advocates', icon: <Users size={24} />, active: false }] : []),
    // Client-only: Recommend Advocate
    ...(userRole === 'client' ? [{ id: 'recommend', label: 'Recommend', icon: <Zap size={24} />, active: false }] : []),
    { id: 'summarize', label: 'Case AI', icon: <FileText size={24} />, active: false },
    { id: 'compliance', label: 'Compliance', icon: <CheckCircle size={24} />, active: false },
  ];

  const recentCases = [
    { id: 1, name: 'Smith vs. Corp Inc.', date: 'Dec 5, 2025', status: 'active' },
    { id: 2, name: 'Estate Settlement Case', date: 'Nov 28, 2025', status: 'pending' },
    { id: 3, name: 'Contract Review', date: 'Nov 15, 2025', status: 'archived' },
  ];

  const stats = [
    { label: 'Active Cases', value: '3', change: '+2' },
    { label: 'Documents', value: '12', change: '+4' },
    { label: 'Advocates', value: '5', change: '+1' },
    { label: 'Compliance', value: '98%', change: '+5%' },
  ];

  const updates = [
    { title: 'Case Summary Generated', time: '2 hours ago' },
    { title: 'Document Compliance Checked', time: '4 hours ago' },
    { title: 'Advocate Profile Updated', time: '1 day ago' },
  ];

  // Reminder checker: runs every minute to scan events and generate notifications
  useEffect(() => {
    if (!events.length) return;

    const checkReminders = () => {
      const now = new Date();
      const newNotifs = [];

      events.forEach(event => {
        if (!event.reminder_minutes || event.reminder_minutes <= 0) return;

        const eventStart = new Date(`${event.start_date}T${event.start_time}`);
        const reminderTime = new Date(eventStart.getTime() - event.reminder_minutes * 60000);

        // If the current time is after the reminder time but before the event starts
        if (now >= reminderTime && now < eventStart) {
          const id = `rem-${event.id}-${reminderTime.getTime()}`;
          newNotifs.push({
            id,
            type: getNotificationTypeFromEvent(event.event_type),
            event_id: event.id,
            message: `Upcoming task: ${event.title.toLowerCase()} (in ${Math.round((eventStart - now) / 60000)} mins)`,
            time: 'Just now'
          });
        }
      });

      if (newNotifs.length > 0) {
        setNotifications(prev => {
          const existing = new Set(prev.map(n => n.id));
          const uniqueNew = newNotifs.filter(n => !existing.has(n.id) && !dismissedNotificationIds[n.id]);
          if (!uniqueNew.length) return prev;
          return mergeNotificationsWithSeenRetention([...uniqueNew, ...prev], prev);
        }); // Keep last 10
      }
    };

    const interval = setInterval(checkReminders, 30000); // Check every 30 seconds
    checkReminders(); // Initial check

    return () => clearInterval(interval);
  }, [events, dismissedNotificationIds, mergeNotificationsWithSeenRetention]);

  // Message notifier: polls unread messages and shows "X messaged you" style notifications.
  useEffect(() => {
    if (!user?.id) return;

    const checkMessageNotifications = async () => {
      const col = userRole === 'lawyer' ? 'lawyer_id' : 'client_id';
      const { data: convs } = await supabase
        .from('conversations')
        .select('id')
        .eq(col, user.id);

      if (!convs?.length) {
        setNotifications(prev => mergeNotificationsWithSeenRetention(
          prev.filter(n => !String(n.id).startsWith('msg-')),
          prev
        ));
        return;
      }

      const convIds = convs.map(c => c.id);
      const { data: unreadMsgs } = await supabase
        .from('messages')
        .select('id, sender_id, content, created_at, conversation_id')
        .in('conversation_id', convIds)
        .eq('is_read', false)
        .neq('sender_id', user.id)
        .order('created_at', { ascending: false })
        .limit(6);

      if (!unreadMsgs?.length) {
        setNotifications(prev => mergeNotificationsWithSeenRetention(
          prev.filter(n => !String(n.id).startsWith('msg-')),
          prev
        ));
        return;
      }

      const senderIds = [...new Set(unreadMsgs.map(m => m.sender_id).filter(Boolean))];
      const senderMap = {};

      if (senderIds.length > 0) {
        const { data: profilesById } = await supabase
          .from('user_profiles')
          .select('id, full_name, email, photo_url')
          .in('id', senderIds);

        const mergeSenderProfile = (resolvedId, profile) => {
          if (!resolvedId) return;
          const existing = senderMap[resolvedId] || {};
          senderMap[resolvedId] = {
            name: existing.name || profile.full_name || profile.email?.split('@')[0] || 'Someone',
            photo_url: existing.photo_url || profile.photo_url || null,
          };
        };

        (profilesById || []).forEach((p) => {
          if (!p?.id) return;
          mergeSenderProfile(p.id, p);
        });

        const unresolvedNameIds = senderIds.filter((id) => !senderMap[id]?.name);
        if (unresolvedNameIds.length > 0) {
          const { data: legacyUsers } = await supabase
            .from('users')
            .select('id, name, email')
            .in('id', unresolvedNameIds);

          (legacyUsers || []).forEach((u) => {
            const existing = senderMap[u.id] || {};
            senderMap[u.id] = {
              name: existing.name || u.name || u.email?.split('@')[0] || 'Someone',
              photo_url: existing.photo_url || null,
            };
          });
        }

        const unresolved = senderIds.filter(id => !senderMap[id]?.name || !senderMap[id]?.photo_url);
        if (unresolved.length > 0) {
          const { data: advs } = await supabase
            .from('advocates')
            .select('user_id, name, email, photo_path')
            .in('user_id', unresolved);
          (advs || []).forEach(a => {
            const existing = senderMap[a.user_id] || {};
            senderMap[a.user_id] = {
              name: existing.name || a.name || a.email?.split('@')[0] || 'Someone',
              photo_url: existing.photo_url || a.photo_path || null,
            };
          });
        }
      }

      const msgNotifs = unreadMsgs.map((m) => {
        const senderInfo = senderMap[m.sender_id] || { name: 'Someone', photo_url: null };
        const sender = senderInfo.name;
        const snippet = (m.content || '').trim();
        const preview = snippet.length > 40 ? `${snippet.slice(0, 40)}...` : snippet;
        const messageText = preview ? `messaged you: ${preview}` : 'messaged you';
        return {
          id: `msg-${m.id}`,
          type: 'message',
          conversation_id: m.conversation_id,
          sender_name: sender,
          photo_url: senderInfo.photo_url,
          message_text: messageText,
          message: `${sender} ${messageText}`,
          time: formatNotificationTime(m.created_at),
          created_at: m.created_at,
        };
      });

      msgNotifs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      setNotifications(prev => {
        const nonMessage = prev.filter(n => !String(n.id).startsWith('msg-'));
        const visibleMessages = msgNotifs.filter((n) => !dismissedNotificationIds[n.id]);
        const visibleNonMessage = nonMessage.filter((n) => !dismissedNotificationIds[n.id]);
        return mergeNotificationsWithSeenRetention([...visibleMessages, ...visibleNonMessage], prev);
      });
    };

    checkMessageNotifications();
    const interval = setInterval(checkMessageNotifications, 15000);
    return () => clearInterval(interval);
  }, [user?.id, userRole, dismissedNotificationIds, mergeNotificationsWithSeenRetention]);

  // Case Workspace notifier: relationship-level unread message checks.
  useEffect(() => {
    if (!user?.id || !clients.length) {
      setWorkspaceUnreadByRelationship({});
      setNotifications((prev) => mergeNotificationsWithSeenRetention(
        prev.filter((n) => !String(n.id).startsWith('cwmsg-')),
        prev
      ));
      return;
    }

    const checkWorkspaceNotifications = async () => {
      const relationshipIds = clients.map((c) => c.id).filter(Boolean);
      if (!relationshipIds.length) return;

      const { data: msgs, error } = await supabase
        .from('case_messages')
        .select('id, relationship_id, sender_id, content, created_at')
        .in('relationship_id', relationshipIds)
        .neq('sender_id', user.id)
        .order('created_at', { ascending: false })
        .limit(200);

      if (error) return;

      const unreadCounts = {};
      const latestUnreadByRelationship = {};

      (msgs || []).forEach((msg) => {
        const relId = msg.relationship_id;
        const seenAt = workspaceLastSeenByRelationship[relId];
        const isUnread = !seenAt || new Date(msg.created_at) > new Date(seenAt);
        if (!isUnread) return;

        unreadCounts[relId] = (unreadCounts[relId] || 0) + 1;
        if (!latestUnreadByRelationship[relId]) {
          latestUnreadByRelationship[relId] = msg;
        }
      });

      setWorkspaceUnreadByRelationship(unreadCounts);

      const relMap = {};
      clients.forEach((c) => { relMap[c.id] = c; });

      const workspaceNotifs = Object.values(latestUnreadByRelationship).map((msg) => {
        const client = relMap[msg.relationship_id] || {};
        const senderName = client.name || 'Someone';
        const roleHint = userRole === 'lawyer' ? 'your client' : 'your lawyer';
        return {
          id: `cwmsg-${msg.relationship_id}`,
          type: 'case_message',
          relationship_id: msg.relationship_id,
          sender_name: senderName,
          photo_url: client.photo_url || null,
          message_text: `(${roleHint}) has messaged in case workspace`,
          message: `${senderName} (${roleHint}) has messaged in case workspace`,
          time: formatNotificationTime(msg.created_at),
          created_at: msg.created_at,
        };
      });

      workspaceNotifs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      setNotifications((prev) => {
        const withoutWorkspace = prev.filter((n) => !String(n.id).startsWith('cwmsg-'));
        const visibleWorkspace = workspaceNotifs.filter((n) => !dismissedNotificationIds[n.id]);
        return mergeNotificationsWithSeenRetention([...visibleWorkspace, ...withoutWorkspace], prev);
      });
    };

    checkWorkspaceNotifications();
    const interval = setInterval(checkWorkspaceNotifications, 15000);
    return () => clearInterval(interval);
  }, [user?.id, userRole, clients, workspaceLastSeenByRelationship, dismissedNotificationIds, mergeNotificationsWithSeenRetention]);

  const handleNavClick = (id) => {
    onNavigate(id);
    setMobileMenuOpen(false);
  };

  const handleEditProfile = () => {
    handleNavClick('profile');
  };

  const handleSidebarLogout = () => {
    if (onLogout) onLogout();
    setMobileMenuOpen(false);
  };

  const visibleNotifications = notifications.filter((notif) => {
    const seenAt = seenNotifications[notif.id];
    if (!seenAt) return true;
    const seenTs = new Date(seenAt).getTime();
    if (Number.isNaN(seenTs)) return true;
    return (nowTick - seenTs) < NOTIFICATION_SEEN_TTL_MS;
  });

  const unseenNotificationsCount = visibleNotifications
    .filter((notif) => !seenNotifications[notif.id]).length;

  const upcomingEvents = getUpcomingEvents();
  const previewUpcomingEvents = upcomingEvents.slice(0, 4);
  const directUnreadMessagesCount = visibleNotifications
    .filter((notif) => String(notif.id).startsWith('msg-') && !seenNotifications[notif.id]).length;
  const caseWorkspaceUnreadCount = Object.values(workspaceUnreadByRelationship)
    .reduce((total, count) => total + (Number(count) || 0), 0);
  const unreadChatsCount = directUnreadMessagesCount + caseWorkspaceUnreadCount;

  const realtimeOverviewCards = [
    {
      id: 'people',
      label: userRole === 'lawyer' ? 'Clients' : 'Lawyers',
      value: clients.length,
      icon: <Users size={20} />,
      watermark: <Users size={112} strokeWidth={1.2} />,
      accent: '#c7c3ea',
      onClick: () => setIsClientsExpanded(true),
    },
    {
      id: 'tasks',
      label: 'To Do Task',
      value: upcomingEvents.length,
      icon: <Calendar size={20} />,
      watermark: <Calendar size={112} strokeWidth={1.2} />,
      accent: '#b8c8ec',
      onClick: () => setIsTasksExpanded(true),
    },
    {
      id: 'chats',
      label: 'Unread chats',
      value: unreadChatsCount,
      icon: <MessageCircle size={20} />,
      watermark: <MessageSquare size={112} strokeWidth={1.2} />,
      accent: '#cbc9e4',
      onClick: () => handleNavClick('messages'),
    },
  ];

  return (
    <div className={`dashboard-page ${hideSidebar ? 'shell-mode' : ''}`}>
      {/* SIDEBAR */}
      {!hideSidebar && (
        <aside className={`dash-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <img src={logo} alt="LawEase" className="dash-logo" />

          <nav className="dash-nav">
            <div className="nav-section">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  className={`nav-item ${item.id === 'dashboard' ? 'active' : ''}`}
                  onClick={() => handleNavClick(item.id)}
                  data-tour={`Sidebar / ${item.label}`}
                >
                  <span className="nav-item-icon">{item.icon}</span>
                  <span className="nav-item-label">{item.label}</span>
                </button>
              ))}
            </div>
          </nav>

        </aside>
      )}

      {/* MAIN CONTENT */}
      <main className="dash-main">
        {!hideSidebar && (
          <button
            className="sidebar-mobile-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        )}

        <div className="dash-header dashboard-top-right-header">
          <div className="header-left" />
          <div className="header-right top-header-right">
            <div className="top-notifications-wrap" ref={topNotificationsRef}>
              <button
                className="icon-button"
                type="button"
                aria-label="Open notifications"
                data-tour="Notifications Bell Icon (Top Right)"
                onClick={() => {
                  setIsTopNotificationsOpen((prev) => !prev);
                  setIsTopProfileMenuOpen(false);
                }}
              >
                <Bell size={18} />
                {unseenNotificationsCount > 0 && (
                  <span className="icon-button-badge">{Math.min(unseenNotificationsCount, 99)}</span>
                )}
              </button>

              {isTopNotificationsOpen && (
                <div className="top-notifications-dropdown" role="menu" aria-label="Notifications menu">
                  <div className="top-notifications-header">
                    <h4>Notifications</h4>
                  </div>
                  <div className="top-notifications-list">
                    {visibleNotifications.length > 0 ? (
                      visibleNotifications.map((notif) => {
                        const isMessageExpanded = !!expandedNotificationMessages[notif.id];
                        const hasLongMessage = (notif.message_text || notif.message || '').length > 72;
                        const isSeen = !!seenNotifications[notif.id];

                        return (
                          <div key={notif.id} className={`notification-item ${isSeen ? 'seen' : ''}`} onClick={() => handleNotificationClick(notif)}>
                            <div className="notification-avatar">{renderNotificationAvatar(notif)}</div>
                            <div className="notification-content">
                              <div className={`notification-message ${isMessageExpanded ? 'expanded' : 'clamped'}`}>
                                {notif.sender_name ? (
                                  <>
                                    <strong className="notification-sender-name">{notif.sender_name}</strong>{' '}
                                    <span>{notif.message_text || notif.message}</span>
                                  </>
                                ) : (
                                  notif.message
                                )}
                              </div>
                              {hasLongMessage && (
                                <button
                                  className="notification-show-more"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    markNotificationSeen(notif.id);
                                    toggleNotificationMessage(notif.id);
                                  }}
                                >
                                  {isMessageExpanded ? 'Show less' : 'Show more'}
                                </button>
                              )}
                              <div className="notification-meta-line">
                                <div className="notification-time">{notif.time}</div>
                                {isSeen && <span className="notification-seen-pill">Seen</span>}
                              </div>
                            </div>
                            <button
                              className="notification-clear-btn"
                              title="Clear notification"
                              onClick={(e) => handleClearNotification(notif.id, e)}
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        );
                      })
                    ) : (
                      <div className="empty-state top-notifications-empty">
                        <p className="empty-state-text">You're all caught up!</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="top-profile-wrap" ref={topProfileRef}>
              <button
                type="button"
                className="top-profile-trigger"
                aria-label="Open profile menu"
                data-tour="Profile Menu (Top Right corner, User icon)"
                onClick={() => {
                  setIsTopProfileMenuOpen((prev) => !prev);
                  setIsTopNotificationsOpen(false);
                }}
              >
                <div
                  className="top-profile-avatar"
                  style={{
                    backgroundImage: profilePhoto ? `url(${profilePhoto})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  {!profilePhoto && userName.charAt(0)}
                </div>
                <div className="top-profile-meta">
                  <span className="top-profile-name">{user?.name || userName}</span>
                  <span className="top-profile-role">{userRole === 'lawyer' ? 'Lawyer' : 'Client'}</span>
                </div>
              </button>

              {isTopProfileMenuOpen && (
                <div className="top-profile-menu" role="menu" aria-label="Profile actions">
                  <button className="sidebar-profile-action" onClick={handleEditProfile}>
                    <Edit3 size={14} /> Edit Profile
                  </button>
                  <button className="sidebar-profile-action danger" onClick={handleSidebarLogout}>
                    <LogOut size={14} /> Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* PAGE CONTENT */}
        <div className="dashboard-content">
          {/* HERO CARD */}
          <div className="hero-card dashboard-item" data-tour="Dashboard Header / Welcome Banner">
            <div className="hero-content">
              <h2 className="hero-title">Hello, {userName}</h2>
              <p className="hero-subtitle">
                {userRole === 'lawyer'
                  ? "Your clients trust you with what matters most to them. Manage their cases with the precision and professionalism they deserve"
                  : "The legal system wasn't built for everyone, but LawEase was. You deserve clarity, not confusion. Let's make sense of your case together."}
              </p>
              {/* Button removed per user request */}
            </div>
            <div className="hero-illustration">
              <Scale size={120} strokeWidth={0.5} className="scales-icon" />
            </div>
          </div>

          <div className="realtime-overview">
            {realtimeOverviewCards.map((card, idx) => (
              <button
                key={card.id}
                type="button"
                className="realtime-card dashboard-item"
                style={{ animationDelay: `${idx * 60}ms`, '--metric-accent': card.accent }}
                onClick={card.onClick}
                data-tour={`Overview / ${card.label}`}
              >
                <div className="realtime-card-watermark" aria-hidden="true">{card.watermark}</div>
                <h3 className="realtime-card-label">{card.label}</h3>
                <div className="realtime-card-bottom">
                  <span className="realtime-card-icon">{card.icon}</span>
                  <span className="realtime-card-value">{card.value}</span>
                </div>
              </button>
            ))}
          </div>

          {/* EXPANDED CALENDAR VIEW */}
          {isCalendarExpanded ? (
            <div className="expanded-calendar-container dashboard-item">
              {/* Expanded Header */}
              <div className="expanded-calendar-header">
                <div className="expanded-header-left">
                  <button className="today-btn" onClick={goToToday}>Today</button>
                  <button className="calendar-btn" onClick={goToPreviousMonth} title="Previous month">
                    <ChevronLeft size={18} />
                  </button>
                  <h3 className="expanded-month-title">
                    {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
                  </h3>
                  <button className="calendar-btn" onClick={goToNextMonth} title="Next month">
                    <ChevronRight size={18} />
                  </button>
                </div>
                <div className="expanded-header-right">
                  <button
                    className="expand-calendar-btn"
                    onClick={() => setIsCalendarExpanded(false)}
                    title="Collapse calendar"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Expanded Body: Calendar + Sidebar */}
              <div className="expanded-calendar-body">
                {/* Calendar Section */}
                <div className="expanded-calendar-main">
                  {/* Day headers MON-SUN */}
                  <div className="expanded-day-headers">
                    {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map((day) => (
                      <div key={day} className="expanded-day-header">{day}</div>
                    ))}
                  </div>

                  {/* Month Grid */}
                  <div className="expanded-month-grid">
                    {(() => {
                      const firstDay = getFirstDayMon(currentMonth);
                      const daysInMonth = getDaysInMonth(currentMonth);
                      const prevMonthDays = getPrevMonthDays(currentMonth);
                      const cells = [];

                      // Previous month trailing days
                      for (let i = firstDay - 1; i >= 0; i--) {
                        const dayNum = prevMonthDays - i;
                        cells.push({ day: dayNum, isOtherMonth: true, key: `prev-${dayNum}` });
                      }

                      // Current month days
                      for (let day = 1; day <= daysInMonth; day++) {
                        cells.push({ day, isOtherMonth: false, key: `cur-${day}` });
                      }

                      // Next month leading days
                      let nextDay = 1;
                      while (cells.length % 7 !== 0 || cells.length < 42) {
                        cells.push({ day: nextDay, isOtherMonth: true, key: `next-${nextDay}` });
                        nextDay++;
                        if (cells.length >= 42) break;
                      }

                      const eventColors = {
                        'court_hearing': { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' },
                        'client_meeting': { bg: '#fef9c3', border: '#eab308', text: '#854d0e' },
                        'filing_deadline': { bg: '#fce7f3', border: '#ec4899', text: '#9d174d' },
                        'trial_date': { bg: '#cffafe', border: '#06b6d4', text: '#155e75' },
                        'contract_review': { bg: '#d1fae5', border: '#10b981', text: '#065f46' },
                        'other': { bg: '#ffedd5', border: '#f97316', text: '#9a3412' },
                      };

                      return cells.map((cell) => {
                        const today = new Date();
                        let isToday = false;
                        let dayEvents = [];

                        if (!cell.isOtherMonth) {
                          const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), cell.day);
                          isToday = date.toDateString() === today.toDateString();
                          dayEvents = getEventsForDate(date);
                        }

                        return (
                          <div
                            key={cell.key}
                            className={`expanded-day-cell ${cell.isOtherMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${dayEvents.length > 0 ? 'has-events' : ''}`}
                            onClick={() => {
                              if (!cell.isOtherMonth) {
                                openEventModal(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), cell.day));
                              }
                            }}
                          >
                            <div className="expanded-day-number">
                              <span className={isToday ? 'today-badge' : ''}>{cell.day}</span>
                            </div>
                            <div className="expanded-day-events">
                              {dayEvents.slice(0, 3).map((event, idx) => {
                                const colors = eventColors[event.event_type] || { bg: '#f3f4f6', border: '#6b7280', text: '#374151' };
                                const formatTime = (t) => {
                                  if (!t) return '';
                                  const [h, m] = t.split(':');
                                  const hour = parseInt(h);
                                  const ampm = hour >= 12 ? 'pm' : 'am';
                                  const h12 = hour % 12 || 12;
                                  return `${h12}:${m} ${ampm}`;
                                };
                                return (
                                  <div
                                    key={idx}
                                    className="expanded-event-label"
                                    style={{ backgroundColor: colors.bg, borderLeftColor: colors.border, color: colors.text }}
                                    onClick={(e) => { e.stopPropagation(); setSelectedEvent(event); }}
                                  >
                                    <span className="expanded-event-title">{event.title}</span>
                                    {event.start_time && (
                                      <span className="expanded-event-time">{formatTime(event.start_time)}</span>
                                    )}
                                  </div>
                                );
                              })}
                              {dayEvents.length > 3 && (
                                <div className="expanded-event-more">+{dayEvents.length - 3} more</div>
                              )}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>

                {/* Upcoming Tasks Sidebar */}
                <div className="expanded-sidebar">
                  <button
                    className="expanded-add-event-btn sidebar-add-btn"
                    onClick={() => openEventModal(new Date())}
                  >
                    <Plus size={16} /> Add New Task
                  </button>

                  <h4 className="expanded-sidebar-title">Upcoming Tasks</h4>

                  <div className="expanded-sidebar-events">
                    {upcomingEvents.length > 0 ? (
                      upcomingEvents.map((event) => {
                        const eventDate = parseLocalDate(event.start_date);
                        const eventColor = {
                          'court_hearing': '#3b82f6',
                          'client_meeting': '#eab308',
                          'filing_deadline': '#ec4899',
                          'trial_date': '#06b6d4',
                          'contract_review': '#10b981',
                          'other': '#f97316'
                        }[event.event_type] || '#6b7280';

                        const eventIcon = {
                          'court_hearing': <Gavel size={18} />,
                          'client_meeting': <Users size={18} />,
                          'filing_deadline': <ClipboardList size={18} />,
                          'trial_date': <Landmark size={18} />,
                          'contract_review': <PenLine size={18} />,
                          'other': <ClipboardList size={18} />
                        }[event.event_type] || <Calendar size={18} />;

                        const formatTime12 = (t) => {
                          if (!t) return '';
                          const [h, m] = t.split(':');
                          const hour = parseInt(h);
                          const ampm = hour >= 12 ? 'pm' : 'am';
                          const h12 = hour % 12 || 12;
                          return `${h12}:${m} ${ampm}`;
                        };

                        return (
                          <div key={event.id} className="sidebar-event-card">
                            <div className="sidebar-event-icon" style={{ backgroundColor: eventColor + '20', color: eventColor }}>
                              {eventIcon}
                            </div>
                            <div className="sidebar-event-info">
                              <div className="sidebar-event-name">{event.title}</div>
                              {event.related_user_id && relatedProfiles[event.related_user_id] && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-3)', marginBottom: 2 }}>
                                  {relatedProfiles[event.related_user_id].photo_url
                                    ? <img src={relatedProfiles[event.related_user_id].photo_url} alt="" style={{ width: 14, height: 14, borderRadius: '50%', objectFit: 'cover' }} />
                                    : <span style={{ width: 14, height: 14, borderRadius: '50%', background: 'var(--primary)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700 }}>{relatedProfiles[event.related_user_id].full_name?.[0]?.toUpperCase()}</span>
                                  }
                                  with {relatedProfiles[event.related_user_id].full_name}
                                </div>
                              )}
                              <div className="sidebar-event-date">
                                {eventDate.toLocaleDateString('default', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                              </div>
                              {isEventOngoing(event) && (
                                <div className="event-live-badge">Pending</div>
                              )}
                              <div className="sidebar-event-time">
                                <strong>{formatTime12(event.start_time)}</strong>
                                {event.start_time && event.end_time && ' - ' + formatTime12(event.end_time)}
                              </div>
                              {event.location && (
                                <div className="sidebar-event-location">{event.location}</div>
                              )}
                              {event.description && (
                                <div className="sidebar-event-desc">{event.description}</div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="empty-state">
                        <p className="empty-state-text">No upcoming tasks.</p>
                      </div>
                    )}
                  </div>

                  <button className="sidebar-see-more-btn">See More</button>
                </div>
              </div>
            </div>
          ) : isClientsExpanded ? (
            <>
              {/* EXPANDED CLIENTS VIEW */}
              <div className="expanded-calendar-container dashboard-item">
                {/* Header */}
                <div className="expanded-calendar-header">
                  <div className="expanded-header-left">
                    <Users size={22} />
                    <h3 className="expanded-month-title">{peopleLabel}</h3>
                    <span style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginLeft: '4px' }}>
                      ({clients.length} total)
                    </span>
                  </div>
                  <div className="expanded-header-right">
                    <button
                      className="expanded-add-event-btn"
                      onClick={() => setShowAddClientForm(true)}
                    >
                      <Plus size={16} /> Add {personLabel}
                    </button>
                    <button
                      className="expand-calendar-btn"
                      onClick={() => { setIsClientsExpanded(false); setSelectedClient(null); }}
                      title={`Collapse ${peopleLabel.toLowerCase()}`}
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                {/* Body: Client List + Detail Sidebar */}
                <div className="expanded-calendar-body">
                  {/* Client List */}
                  <div className="expanded-calendar-main" style={{ padding: 'var(--spacing-lg)', overflowY: 'auto' }}>
                    <div className="clients-list">
                      {clients.length > 0 ? (
                        clients.map((client) => {
                          const meetings = getClientMeetings(client.name);
                          const workspaceUnread = workspaceUnreadByRelationship[client.id] || 0;
                          return (
                            <div
                              key={client.id}
                              className={`client-card ${selectedClient?.id === client.id ? 'selected' : ''}`}
                              onClick={() => openWorkspaceForRelationship(client)}
                            >
                              <div className="client-card-avatar">
                                <ClientAvatar name={client.name} photoUrl={client.photo_url} size={40} />
                                {workspaceUnread > 0 && (
                                  <span className="client-workspace-unread-badge" title="Unread case workspace message">
                                    <MessageSquare size={10} />
                                  </span>
                                )}
                              </div>
                              <div className="client-card-info">
                                <div className="client-card-name">{client.name}</div>
                                <div className="client-card-case">
                                  {userRole === 'lawyer' ? (client.caseType || 'No case assigned') : (client.specialization || 'General Practice')}
                                </div>
                                {meetings.length > 0 && (
                                  <div className="client-card-meetings">
                                    <Calendar size={12} /> {meetings.length} meeting{meetings.length > 1 ? 's' : ''}
                                  </div>
                                )}
                              </div>
                              <div className="client-card-right">
                                <span className={`case-status ${client.status}`}>
                                  {client.status.charAt(0).toUpperCase() + client.status.slice(1)}
                                </span>
                                <button
                                  className="client-delete-btn"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteClient(client.id); }}
                                  title={`Remove ${personLabel.toLowerCase()}`}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="empty-state">
                          <div className="empty-state-icon">👥</div>
                          <p className="empty-state-text">No {peopleLabel.toLowerCase()} yet. Click "Add {personLabel}" to get started.</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Client Detail Sidebar */}
                  <div className="expanded-sidebar">
                    {selectedClient ? (
                      <>
                        <div className="client-detail-header">
                          <div className="client-detail-avatar" style={{ background: 'transparent' }}>
                            <ClientAvatar
                              name={selectedClient.name}
                              photoUrl={selectedClient.photo_url}
                              size={56}
                              fontSize={20}
                            />
                          </div>
                          <h4 className="expanded-sidebar-title">{selectedClient.name}</h4>
                          <span className={`case-status ${selectedClient.status}`}>
                            {selectedClient.status.charAt(0).toUpperCase() + selectedClient.status.slice(1)}
                          </span>
                        </div>

                        <div className="client-detail-section">
                          <h5 className="client-section-title">Contact</h5>
                          {selectedClient.email && (
                            <div className="client-detail-row">
                              <Mail size={14} /> <span>{selectedClient.email}</span>
                            </div>
                          )}
                          {selectedClient.phone && (
                            <div className="client-detail-row">
                              <Phone size={14} /> <span>{selectedClient.phone}</span>
                            </div>
                          )}
                        </div>

                        {userRole === 'lawyer' ? (
                          <div className="client-detail-section">
                            <h5 className="client-section-title">Case Details</h5>
                            <div className="client-case-type-badge">{selectedClient.caseType || 'Unassigned'}</div>
                            {selectedClient.caseDetails && (
                              <p className="client-case-description">{selectedClient.caseDetails}</p>
                            )}
                          </div>
                        ) : (
                          <div className="client-detail-section">
                            <h5 className="client-section-title">Specialization & Experience</h5>
                            <div className="client-case-type-badge">{selectedClient.specialization || 'General Practice'}</div>
                            <div className="client-detail-row" style={{ marginTop: '8px' }}>
                              <strong>Bar Council ID:</strong> <span>{selectedClient.barCouncilId || 'N/A'}</span>
                            </div>
                            <div className="client-detail-row" style={{ marginTop: '4px' }}>
                              <strong>Experience:</strong> <span>{selectedClient.experience || 'Not specified'}</span>
                            </div>
                          </div>
                        )}

                        <div className="client-detail-section">
                          <h5 className="client-section-title">
                            Scheduled Meetings
                            <button
                              className="client-add-meeting-btn"
                              onClick={() => openEventModal(new Date())}
                              title="Schedule meeting"
                            >
                              <Plus size={14} />
                            </button>
                          </h5>
                          {(() => {
                            const meetings = getClientMeetings(selectedClient.name);
                            if (meetings.length === 0) {
                              return (
                                <p className="client-no-meetings">No meetings scheduled. Add one from the calendar.</p>
                              );
                            }
                            return meetings.map((meeting) => {
                              const meetingDate = parseLocalDate(meeting.start_date);
                              const fmt = (t) => {
                                if (!t) return '';
                                const [h, m] = t.split(':');
                                const hour = parseInt(h);
                                return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
                              };
                              return (
                                <div key={meeting.id} className="client-meeting-card" onClick={() => setSelectedEvent(meeting)}>
                                  <div className="client-meeting-dot" style={{
                                    backgroundColor: {
                                      'court_hearing': '#3b82f6', 'client_meeting': '#eab308',
                                      'filing_deadline': '#ec4899', 'trial_date': '#06b6d4',
                                      'contract_review': '#10b981', 'other': '#f97316'
                                    }[meeting.event_type] || '#6b7280'
                                  }} />
                                  <div className="client-meeting-info">
                                    <div className="client-meeting-title">{meeting.title}</div>
                                    <div className="client-meeting-date">
                                      {meetingDate.toLocaleDateString('default', { month: 'short', day: 'numeric' })} · {fmt(meeting.start_time)}
                                    </div>
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </>
                    ) : (
                      <div className="client-sidebar-empty">
                        <UserIcon size={48} strokeWidth={1} />
                        <p>Select a {personLabel.toLowerCase()} to view details</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ADD CLIENT FORM OVERLAY */}
              {showAddClientForm && (
                <div className="event-detail-overlay" onClick={() => setShowAddClientForm(false)}>
                  <div className="event-detail-popup" onClick={(e) => e.stopPropagation()}>
                    <button className="event-detail-close" onClick={() => setShowAddClientForm(false)}>
                      <X size={20} />
                    </button>
                    <div className="event-detail-header">
                      <div className="event-detail-color-bar" style={{ backgroundColor: 'var(--color-primary)' }} />
                      <div className="event-detail-title-group">
                        <h3 className="event-detail-title">Add New {personLabel}</h3>
                      </div>
                    </div>
                    <div className="event-detail-body">
                      <div className="add-client-field">
                        <label>Full Name *</label>
                        <input type="text" value={newClient.name} onChange={e => setNewClient({ ...newClient, name: e.target.value })} placeholder="e.g. John Smith" />
                      </div>
                      <div className="add-client-field">
                        <label>Email</label>
                        <input type="email" value={newClient.email} onChange={e => setNewClient({ ...newClient, email: e.target.value })} placeholder="john@email.com" />
                      </div>
                      <div className="add-client-field">
                        <label>Phone</label>
                        <input type="tel" value={newClient.phone} onChange={e => setNewClient({ ...newClient, phone: e.target.value })} placeholder="+1 555-0000" />
                      </div>
                      {userRole === 'lawyer' ? (
                        <>
                          <div className="add-client-field">
                            <label>Case Type</label>
                            <input type="text" value={newClient.caseType} onChange={e => setNewClient({ ...newClient, caseType: e.target.value })} placeholder="e.g. Corporate Law" />
                          </div>
                          <div className="add-client-field">
                            <label>Case Details</label>
                            <textarea value={newClient.caseDetails} onChange={e => setNewClient({ ...newClient, caseDetails: e.target.value })} placeholder="Brief description of the case..." rows={3} />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="add-client-field">
                            <label>Specialization</label>
                            <input type="text" value={newClient.specialization} onChange={e => setNewClient({ ...newClient, specialization: e.target.value })} placeholder="e.g. Corporate Law" />
                          </div>
                          <div className="add-client-field">
                            <label>Bar Council ID</label>
                            <input type="text" value={newClient.barCouncilId} onChange={e => setNewClient({ ...newClient, barCouncilId: e.target.value })} placeholder="e.g. PBC-1234" />
                          </div>
                          <div className="add-client-field">
                            <label>Experience</label>
                            <input type="text" value={newClient.experience} onChange={e => setNewClient({ ...newClient, experience: e.target.value })} placeholder="e.g. 10 years" />
                          </div>
                        </>
                      )}
                    </div>
                    <div className="event-detail-actions">
                      <button className="event-detail-close-btn" style={{ backgroundColor: 'var(--color-text-secondary)' }} onClick={() => setShowAddClientForm(false)}>
                        Cancel
                      </button>
                      <button className="event-detail-close-btn" onClick={handleAddClient}>
                        Add {personLabel}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : isTasksExpanded ? (
            <div className="expanded-calendar-container dashboard-item">
              <div className="tasks-expanded-header">
                <div className="tasks-expanded-header-left">
                  <h3 className="tasks-expanded-title">My Tasks</h3>
                  <span className="tasks-expanded-subtitle">Manage all of your tasks here.</span>
                </div>
                <div className="tasks-expanded-header-right">
                  <button
                    className="tasks-add-btn"
                    onClick={() => openEventModal(new Date())}
                  >
                    <Plus size={15} /> New
                  </button>
                  <button
                    className="tasks-collapse-btn"
                    onClick={() => setIsTasksExpanded(false)}
                    title="Collapse"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              <div className="tasks-table-wrap">
                <table className="tasks-table">
                  <thead>
                    <tr>
                      <th className="tasks-th tasks-th-name">Task Name</th>
                      <th className="tasks-th">Type</th>
                      <th className="tasks-th">Assignee</th>
                      <th className="tasks-th">Due Date</th>
                      <th className="tasks-th">Status</th>
                      <th className="tasks-th tasks-th-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingEvents.length > 0 ? (
                      upcomingEvents.map((event) => {
                        const eventDate = parseLocalDate(event.start_date);
                        const eventColor = {
                          'court_hearing': '#3b82f6',
                          'client_meeting': '#eab308',
                          'filing_deadline': '#ec4899',
                          'trial_date': '#06b6d4',
                          'contract_review': '#10b981',
                          'other': '#f97316'
                        }[event.event_type] || '#6b7280';

                        const typeLabel = (event.event_type || 'other')
                          .replace(/_/g, ' ')
                          .replace(/\b\w/g, c => c.toUpperCase());

                        const fmt = (t) => {
                          if (!t) return '';
                          const [h, m] = t.split(':');
                          const hour = parseInt(h);
                          return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
                        };

                        const statusLabel = isEventOngoing(event) ? 'Pending' : 'Upcoming';
                        const statusClass = isEventOngoing(event) ? 'status-pending' : 'status-upcoming';

                        const related = event.related_user_id && relatedProfiles[event.related_user_id];

                        return (
                          <tr key={event.id} className="tasks-row" onClick={() => setSelectedEvent(event)}>
                            <td className="tasks-td tasks-td-name">
                              <div className="tasks-name-cell">
                                <span className="tasks-color-dot" style={{ backgroundColor: eventColor }} />
                                <span className="tasks-name-text">{event.title}</span>
                              </div>
                            </td>
                            <td className="tasks-td">
                              <span className="tasks-type-badge" style={{ backgroundColor: eventColor + '18', color: eventColor, borderColor: eventColor + '40' }}>
                                {typeLabel}
                              </span>
                            </td>
                            <td className="tasks-td">
                              {related ? (
                                <span className="tasks-assignee-name">{related.full_name}</span>
                              ) : (
                                <span className="tasks-assignee-name">You</span>
                              )}
                            </td>
                            <td className="tasks-td tasks-td-date">
                              <span className={isEventOngoing(event) ? 'tasks-date-ongoing' : ''}>
                                {eventDate.toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' })}
                                {event.start_time && <span className="tasks-time-hint">, {fmt(event.start_time)}</span>}
                              </span>
                            </td>
                            <td className="tasks-td">
                              <span className={`tasks-status-badge ${statusClass}`}>{statusLabel}</span>
                            </td>
                            <td className="tasks-td tasks-td-actions" onClick={(e) => e.stopPropagation()}>
                              <div className="tasks-row-actions">
                                <button className="tasks-row-btn" onClick={() => handleRescheduleEvent(event)} title="Reschedule">
                                  Reschedule
                                </button>
                                <button className="tasks-row-btn tasks-row-btn-done" onClick={() => handleMarkEventCompleted(event)} title="Done">
                                  Done
                                </button>
                                <button className="tasks-row-btn tasks-row-btn-delete" onClick={() => handleDeleteEvent(event.id)} title="Delete">
                                  <X size={13} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={6} className="tasks-empty-row">
                          <div className="empty-state">
                            <p className="empty-state-text">No upcoming tasks. Click "+ New" to add one!</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            /* COMPACT CONTENT GRID (original layout) */
            <div className="content-grid">
              {/* LEFT COLUMN */}
              <div className="left-column">

                {/* CLIENTS / LAWYERS WIDGET — replaces Recent Cases */}
                <div className="panel dashboard-item people-panel" data-tour="Clients Panel (Left Sidebar or Card)">
                  <div className="panel-header">
                    <h3 className="panel-title">{peopleLabel}</h3>
                    <button className="expand-calendar-btn" onClick={() => setIsClientsExpanded(true)} title={`Expand ${peopleLabel.toLowerCase()}`}>
                      <Maximize2 size={16} />
                    </button>
                  </div>
                  <div className="panel-body">
                    {clients.length > 0 ? (
                      clients.slice(0, 5).map((client) => {
                        const workspaceUnread = workspaceUnreadByRelationship[client.id] || 0;
                        return (
                          <div
                            key={client.id}
                            className="case-item"
                            style={{ cursor: 'pointer' }}
                            onClick={() => openWorkspaceForRelationship(client)}
                          >
                            <div className="client-avatar-wrap" style={{ marginRight: 10 }}>
                              <ClientAvatar name={client.name} photoUrl={client.photo_url} size={32} />
                              {workspaceUnread > 0 && (
                                <span className="client-workspace-unread-badge" title="Unread case workspace message">
                                  <MessageSquare size={10} />
                                </span>
                              )}
                            </div>
                            <div className="case-info">
                              <div className="case-name">{client.name}</div>
                              <div className="case-meta">{userRole === 'lawyer' ? client.caseType : client.specialization}</div>
                            </div>
                            <span className={`case-status ${client.status}`}>
                              {client.status.charAt(0).toUpperCase() + client.status.slice(1)}
                            </span>
                          </div>
                        );
                      })
                    ) : (
                      <div className="empty-state">
                        <div className="empty-state-icon">👥</div>
                        <p className="empty-state-text">No {peopleLabel.toLowerCase()} yet. Add your first {personLabel.toLowerCase()}!</p>
                      </div>
                    )}
                    {clients.length > 5 && (
                      <button className="panel-action" style={{ marginTop: '8px', display: 'block', width: '100%', textAlign: 'center' }} onClick={() => setIsClientsExpanded(true)}>
                        View all {clients.length} {peopleLabel.toLowerCase()}
                      </button>
                    )}
                  </div>
                </div>

                {/* UPCOMING TASKS WIDGET */}
                <div className="upcoming-events-widget dashboard-item" data-tour="Upcoming Tasks widget (Center)">
                  <div className="upcoming-header">
                    <h4 className="upcoming-title">Upcoming Tasks</h4>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <button
                        className="expand-calendar-btn"
                        onClick={() => setIsTasksExpanded(true)}
                        title="Expand tasks"
                      >
                        <Maximize2 size={16} />
                      </button>
                      <button
                        className="add-event-btn"
                        onClick={() => openEventModal(new Date())}
                        title="Create new task"
                        data-tour="Add Client Button (+ icon in Clients section)"
                      >
                        <Plus size={16} /> New
                      </button>
                    </div>
                  </div>
                  <div className="upcoming-body">
                    {upcomingEvents.length > 0 ? (
                      previewUpcomingEvents.map((event) => {
                        const eventDate = parseLocalDate(event.start_date);
                        const eventColor = {
                          'court_hearing': '#3b82f6',
                          'client_meeting': '#eab308',
                          'filing_deadline': '#ec4899',
                          'trial_date': '#06b6d4',
                          'contract_review': '#10b981',
                          'other': '#f97316'
                        }[event.event_type] || '#6b7280';

                        return (
                          <div key={event.id} className="upcoming-event" onClick={() => setSelectedEvent(event)} style={{ cursor: 'pointer' }}>
                            <div
                              className="upcoming-event-type"
                              style={{ backgroundColor: eventColor }}
                            />
                            <div className="upcoming-event-content">
                              <div className="upcoming-event-title">{event.title}</div>
                              {event.related_user_id && relatedProfiles[event.related_user_id] && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-3)', marginBottom: 2 }}>
                                  {relatedProfiles[event.related_user_id].photo_url
                                    ? <img src={relatedProfiles[event.related_user_id].photo_url} alt="" style={{ width: 16, height: 16, borderRadius: '50%', objectFit: 'cover' }} />
                                    : <span style={{ width: 16, height: 16, borderRadius: '50%', background: 'var(--primary)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700 }}>{relatedProfiles[event.related_user_id].full_name?.[0]?.toUpperCase()}</span>
                                  }
                                  with {relatedProfiles[event.related_user_id].full_name}
                                </div>
                              )}
                              <div className="upcoming-event-date">
                                {eventDate.toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </div>
                              {isEventOngoing(event) && (
                                <div className="event-live-badge">Pending</div>
                              )}
                              <div className="upcoming-event-time">
                                <Clock size={12} /> {event.start_time} - {event.end_time}
                              </div>
                              {event.location && (
                                <div className="upcoming-event-location">{event.location}</div>
                              )}
                            </div>
                            <div className="upcoming-event-actions" onClick={(e) => e.stopPropagation()}>
                              <button
                                className="upcoming-event-action"
                                onClick={() => handleRescheduleEvent(event)}
                                title="Reschedule task"
                              >
                                Reschedule
                              </button>
                              <button
                                className="upcoming-event-action success"
                                onClick={() => handleMarkEventCompleted(event)}
                                title="Mark as completed"
                              >
                                Done
                              </button>
                              <button
                                className="upcoming-event-action danger"
                                onClick={() => handleDeleteEvent(event.id)}
                                title="Delete task"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="empty-state">
                        <p className="empty-state-text">No upcoming tasks. Click a date on the calendar to add one!</p>
                      </div>
                    )}
                    {upcomingEvents.length > 4 && (
                      <button className="panel-action" style={{ marginTop: '8px', alignSelf: 'flex-start' }} onClick={() => setIsTasksExpanded(true)}>
                        View all {upcomingEvents.length} tasks
                      </button>
                    )}
                  </div>
                </div>


              </div>

              {/* RIGHT COLUMN */}
              <div className="right-column">
                {/* CALENDAR WIDGET */}
                <div className="calendar-widget dashboard-item" data-tour="Calendar Panel (Right side)">
                  <div className="calendar-header">
                    <div className="calendar-nav-header">
                      <div className="calendar-nav-left">
                        <button className="calendar-btn" onClick={goToPreviousMonth} title="Previous month">
                          <ChevronLeft size={18} />
                        </button>
                        <h4 className="calendar-title">
                          {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
                        </h4>
                        <button className="calendar-btn" onClick={goToNextMonth} title="Next month">
                          <ChevronRight size={18} />
                        </button>
                      </div>
                      <button
                        className="expand-calendar-btn"
                        onClick={() => setIsCalendarExpanded(true)}
                        title="Expand calendar"
                      >
                        <Calendar size={20} />
                      </button>
                    </div>
                  </div>

                  {/* Day headers */}
                  <div className="calendar-day-headers">
                    {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map((day) => (
                      <div key={day} className="calendar-day-header">{day}</div>
                    ))}
                  </div>

                  {/* Calendar grid */}
                  <div className="calendar-grid">
                    {(() => {
                      const firstDay = getFirstDayOfMonth(currentMonth);
                      const daysInMonth = getDaysInMonth(currentMonth);
                      const days = [];

                      // Previous month's days
                      for (let i = firstDay - 1; i >= 0; i--) {
                        days.push(null);
                      }

                      // Current month's days
                      for (let day = 1; day <= daysInMonth; day++) {
                        days.push(day);
                      }

                      // Next month's days to fill the grid
                      while (days.length % 7 !== 0) {
                        days.push(null);
                      }

                      return days.map((day, idx) => {
                        if (day === null) {
                          return <div key={`empty-${idx}`} className="calendar-day other-month"></div>;
                        }

                        const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                        const dayEvents = getEventsForDate(date);
                        const today = new Date();
                        const isToday = date.toDateString() === today.toDateString();

                        return (
                          <div
                            key={day}
                            className={`calendar-day ${isToday ? 'today' : ''} ${dayEvents.length > 0 ? 'has-events' : ''}`}
                            onClick={() => openEventModal(date)}
                          >
                            <div className="calendar-day-number">{day}</div>
                            {dayEvents.length > 0 && (
                              <div className="calendar-day-events">
                                {dayEvents.slice(0, 2).map((event, idx) => (
                                  <div key={idx} className={`event-dot event-${event.event_type}`} title={event.title} />
                                ))}
                                {dayEvents.length > 2 && <div className="event-dot-more">+{dayEvents.length - 2}</div>}
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>

                {/* Sticky Reminder Pad */}
                {user?.id && (
                  <div className="panel dashboard-item sticky-note-panel" data-tour="Sticky Notes">
                    <div className="panel-header">
                      <h3 className="panel-title sticky-note-title">
                        <PenLine size={16} /> Sticky Notes
                      </h3>
                      {clientReminderNote.length > 0 && (
                        <button className="panel-action" onClick={() => setClientReminderNote('')}>
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="sticky-note-body">
                      <textarea
                        className="sticky-note-textarea"
                        placeholder="Just start here.."
                        value={clientReminderNote}
                        maxLength={CLIENT_REMINDER_MAX_CHARS}
                        onChange={(e) => setClientReminderNote(e.target.value)}
                      />
                      <div className="sticky-note-footer">
                        <span>{clientReminderNote.length}/{CLIENT_REMINDER_MAX_CHARS}</span>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>
          )}
        </div>

        {/* ADD EVENT MODAL */}
        <AddEventModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleAddEvent}
          selectedDate={selectedDate}
          userRole={userRole}
          clients={clients}
          events={events}
        />

        {/* RESCHEDULE EVENT MODAL */}
        {isRescheduleModalOpen && (
          <div className="modal-overlay" onClick={closeRescheduleModal}>
            <div className="modal-dialog modal-clean" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close-btn" onClick={closeRescheduleModal} aria-label="Close reschedule modal">
                <X size={22} />
              </button>

              <form onSubmit={handleRescheduleSubmit} className="event-form-clean">
                <div className="form-section">
                  <label className="form-label">Task</label>
                  <input
                    className="form-input"
                    value={rescheduleTargetEvent?.title || ''}
                    disabled
                    readOnly
                  />
                </div>

                <div className="form-section">
                  <label className="form-label">Reschedule Date *</label>
                  <input
                    type="date"
                    className="form-input"
                    value={rescheduleForm.start_date}
                    onChange={(e) => setRescheduleForm(prev => ({ ...prev, start_date: e.target.value }))}
                    disabled={isRescheduling}
                  />
                </div>

                <div className="form-section form-row">
                  <div className="form-col">
                    <label className="form-label">Start Time *</label>
                    <input
                      type="time"
                      className="form-input"
                      value={rescheduleForm.start_time}
                      onChange={(e) => setRescheduleForm(prev => ({ ...prev, start_time: e.target.value }))}
                      disabled={isRescheduling}
                    />
                  </div>
                  <div className="form-col">
                    <label className="form-label">End Time</label>
                    <input
                      type="time"
                      className="form-input"
                      value={rescheduleForm.end_time}
                      onChange={(e) => setRescheduleForm(prev => ({ ...prev, end_time: e.target.value }))}
                      disabled={isRescheduling}
                    />
                  </div>
                </div>

                {rescheduleError && <div className="form-error">{rescheduleError}</div>}

                <div className="form-actions-clean">
                  <button type="button" className="btn-secondary-clean" onClick={closeRescheduleModal} disabled={isRescheduling}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary-clean" disabled={isRescheduling}>
                    {isRescheduling ? 'Saving...' : 'Save Reschedule'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* EVENT DETAIL POPUP */}
        {selectedEvent && (
          <div className="event-detail-overlay" onClick={() => setSelectedEvent(null)}>
            <div className="event-detail-popup" onClick={(e) => e.stopPropagation()}>
              <button className="event-detail-close" onClick={() => setSelectedEvent(null)}>
                <X size={20} />
              </button>
              <div className="event-detail-header">
                <div
                  className="event-detail-color-bar"
                  style={{
                    backgroundColor: {
                      'court_hearing': '#3b82f6',
                      'client_meeting': '#eab308',
                      'filing_deadline': '#ec4899',
                      'trial_date': '#06b6d4',
                      'contract_review': '#10b981',
                      'other': '#f97316'
                    }[selectedEvent.event_type] || '#6b7280'
                  }}
                />
                <div className="event-detail-title-group">
                  <h3 className="event-detail-title">{selectedEvent.title}</h3>
                  <span className="event-detail-type">
                    {selectedEvent.event_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </span>
                </div>
              </div>
              <div className="event-detail-body">
                <div className="event-detail-row">
                  <Calendar size={16} />
                  <span>
                    {parseLocalDate(selectedEvent.start_date).toLocaleDateString('default', {
                      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
                    })}
                  </span>
                </div>
                {selectedEvent.start_time && (
                  <div className="event-detail-row">
                    <Clock size={16} />
                    <span>
                      {(() => {
                        const fmt = (t) => {
                          const [h, m] = t.split(':');
                          const hour = parseInt(h);
                          return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
                        };
                        return `${fmt(selectedEvent.start_time)}${selectedEvent.end_time ? ' – ' + fmt(selectedEvent.end_time) : ''}`;
                      })()}
                    </span>
                  </div>
                )}
                {selectedEvent.location && (
                  <div className="event-detail-row">
                    <Briefcase size={16} />
                    <span>{selectedEvent.location}</span>
                  </div>
                )}
                {selectedEvent.related_user_id && relatedProfiles[selectedEvent.related_user_id] && (
                  <div className="event-detail-row">
                    <Users size={16} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {relatedProfiles[selectedEvent.related_user_id].photo_url
                        ? <img src={relatedProfiles[selectedEvent.related_user_id].photo_url} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} />
                        : <span style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--primary)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>{relatedProfiles[selectedEvent.related_user_id].full_name?.[0]?.toUpperCase()}</span>
                      }
                      <span>{relatedProfiles[selectedEvent.related_user_id].full_name}</span>
                    </div>
                  </div>
                )}
                {selectedEvent.description && (
                  <div className="event-detail-description">
                    <p>{selectedEvent.description}</p>
                  </div>
                )}
              </div>
              <div className="event-detail-actions">
                <button className="event-detail-reschedule" onClick={() => handleRescheduleEvent(selectedEvent)}>
                  Reschedule
                </button>
                <button className="event-detail-complete" onClick={() => { handleMarkEventCompleted(selectedEvent); setSelectedEvent(null); }}>
                  Mark Completed
                </button>
                <button className="event-detail-delete" onClick={() => { handleDeleteEvent(selectedEvent.id); setSelectedEvent(null); }}>
                  Delete
                </button>
                <button className="event-detail-close-btn" onClick={() => setSelectedEvent(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* FOOTER */}
        <footer className="dashboard-footer">
          <p>
            © 2025 LawEase. All rights reserved. |{' '}
            <a href="/legal/terms-of-service.html" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
              Terms of Service
            </a>{' '}
            |{' '}
            <a href="/legal/privacy-policy.html" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
              Privacy Policy
            </a>
          </p>
        </footer>
      </main>
    </div>
  );
}